import { Button } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { StudyGroup } from '@prisma/client';
import type { ParticipantBdmStateResDto } from '@web/common';
import { StepKey } from './stepTypes';

export default function getStepperActionButton(
  currentStepKey: StepKey,
  baseLineSurveyLink: string | undefined,
  loadingBaselineState: boolean,
  bdmState: ParticipantBdmStateResDto | null,
  bdm1SurveyDue: boolean,
): JSX.Element {
  if (currentStepKey === StepKey.Step0 && baseLineSurveyLink) {
    return (
      <SurveyButton
        link={baseLineSurveyLink}
        isLoading={loadingBaselineState}
      />
    );
  }
  // step 2 shows the survey-1 button, but only GROUP_1 does the BDM surveys
  if (
    currentStepKey === StepKey.Step2 &&
    bdmState?.studyGroup === StudyGroup.GROUP_1
  ) {
    const survey = bdmState.bdm1Survey;
    // once the survey is filled out there is no action here — the "thank you"
    // is the BDM1 result step (step-bdm1), which has translated copy
    if (survey?.filledOutAt) {
      return <div />;
    }
    // an imported link alone shows nothing — the day-0 offset decides; until
    // then the button behaves like "no link assigned yet"
    return (
      <WillingnessToPaySurveyButton
        link={bdm1SurveyDue ? (survey?.link ?? null) : null}
      />
    );
  }
  // step-bdm1 is the BDM1 thank-you/result only — survey 2 lives on its own
  // step. An imported outcome outranks filledOutAt: it proves the survey was
  // answered even when the fillout was never recorded, so the button goes away
  // and the result copy above it stands alone. Without an outcome the step is
  // only reached while the survey is due (the store gates it on the day-28
  // offset), so the button needs no additional date check here.
  if (currentStepKey === StepKey.StepBdm2) {
    const survey = bdmState?.bdm2Survey ?? null;
    const outcome = bdmState?.bdm2Outcome ?? null;
    if (survey?.link && survey.filledOutAt === null && outcome === null) {
      return <WillingnessToPaySurveyButton link={survey.link} />;
    }
    return <div />;
  }
  return <div />;
}

const SurveyButton = ({
  link,
  isLoading,
}: {
  link: string;
  isLoading: boolean;
}) => {
  return (
    <Button
      w="fit-content"
      mt="md"
      loading={isLoading}
      onClick={() => (window.location.href = link)}
    >
      Fragebogen ausfüllen
    </Button>
  );
};

const WillingnessToPaySurveyButton = ({ link }: { link: string | null }) => {
  return (
    <Button
      w="fit-content"
      mt="md"
      onClick={() => {
        if (link) {
          window.location.href = link;
        } else {
          showNotification({
            message:
              'Dir wurde noch kein Link zugeteilt, bitte versuche es später erneut oder melde dich beim Support',
            color: 'yellow',
          });
        }
      }}
    >
      Zur Umfrage
    </Button>
  );
};
